import { transformEnumToDisplay } from '@fruitful/member-profile';
import { InsuranceCoverage } from '@fruitful/types';
import { zodResolver } from '@hookform/resolvers/zod';
import { Redirect, router, useLocalSearchParams } from 'expo-router';
import { useCallback, useState } from 'react';
import { type SubmitHandler, useForm } from 'react-hook-form';
import {
    BodyMediumRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    Form,
    ScreenContainer,
    SelectMultiple,
    Spinner,
    USE_FORM_MODE_DEFAULTS,
    useToastController,
    YStack,
} from 'smoothie';
import { z } from 'zod';

import { useAuthedUser } from '../../common/providers/AuthedUserProvider';
import LogRocketFacade from '../../logger/log-rocket/LogRocketFacade';
import { captureException } from '../../logger/Logger';
import useUpdateUser from '../../user/hooks/useUpdateUser';
import { useUser } from '../../user/providers/UserProvider';
import { useTrackFinancialProfileCompletion } from '../hooks/useTrackFinancialProfileCompletion';
import { getMemberProfileUserSubtitle } from '../utils/memberProfileUtils';
import {
    SUCCESS_TOAST_DURATION,
    SUCCESS_TOAST_MESSAGE,
    SuccessToastIcon,
} from './SuccessToast';

const insuranceSchema = z.object({
    insuranceCoverage: z
        .array(z.nativeEnum(InsuranceCoverage))
        .min(1, 'Please select at least one option'),
});

type InsuranceSchema = z.infer<typeof insuranceSchema>;

export const Insurance = () => {
    LogRocketFacade.tagPage('Profile/Insurance');

    const toast = useToastController();
    const [isSubmitting, setIsSubmitting] = useState(false);

    const authedUser = useAuthedUser();
    const { userId } = useLocalSearchParams<{ userId?: string }>();

    // Use provided userId or fallback to authenticated user. Find the target user.
    const targetUserId = userId ?? authedUser.id;
    const targetUser = [authedUser, authedUser.partnerMember].find(
        (user) => user?.id === targetUserId,
    );

    const { refetch: refetchUser } = useUser();
    const { mutateAsync: updateUser, isPending: isUpdatingUser } =
        useUpdateUser();

    const { trackFinancialProfileUpdate } =
        useTrackFinancialProfileCompletion();

    const {
        handleSubmit,
        formState: { isValid },
        setValue,
        watch,
    } = useForm<InsuranceSchema>({
        ...USE_FORM_MODE_DEFAULTS,
        resolver: zodResolver(insuranceSchema),
        defaultValues: {
            insuranceCoverage: targetUser?.insuranceCoverage ?? [],
        },
    });

    const getDisplayLabel = (option: InsuranceCoverage): string => {
        return option === InsuranceCoverage.NONE
            ? 'None of the above'
            : transformEnumToDisplay(option);
    };

    const insuranceOptions = Object.values(InsuranceCoverage).map(
        (coverage) => ({
            title: getDisplayLabel(coverage),
        }),
    );

    const watchedInsuranceCoverage = watch('insuranceCoverage');

    const onSubmit: SubmitHandler<InsuranceSchema> = useCallback(
        async (data) => {
            if (targetUser == null) {
                captureException(
                    new Error('Target user not found for insurance update'),
                );
                return;
            }

            setIsSubmitting(true);

            try {
                await updateUser({
                    urlParams: { userId: targetUserId },
                    body: {
                        insuranceCoverage: data.insuranceCoverage,
                    },
                });

                toast.show(SUCCESS_TOAST_MESSAGE, {
                    duration: SUCCESS_TOAST_DURATION,
                    icon: SuccessToastIcon,
                });

                void refetchUser?.();

                trackFinancialProfileUpdate({
                    insuranceUpdated: data.insuranceCoverage.length > 0,
                });

                router.back();
            } catch (error) {
                captureException(error);
                toast.show('Something went wrong!', { duration: 2000 });
            } finally {
                setIsSubmitting(false);
            }
        },
        [
            targetUserId,
            updateUser,
            toast,
            refetchUser,
            trackFinancialProfileUpdate,
        ],
    );

    const handleInsuranceToggle = useCallback(
        (index: number) => {
            const coverageOptions = Object.values(InsuranceCoverage);
            const coverage = coverageOptions[index];

            if (coverage == null) {
                return;
            }

            const currentCoverage = watchedInsuranceCoverage ?? [];
            const isCurrentlySelected = currentCoverage.includes(coverage);

            if (coverage === InsuranceCoverage.NONE) {
                // If "None" is selected, clear all other selections
                if (isCurrentlySelected) {
                    // If "None" is currently selected, deselect it
                    setValue('insuranceCoverage', [], { shouldValidate: true });
                } else {
                    // If "None" is not selected, select only "None"
                    setValue('insuranceCoverage', [InsuranceCoverage.NONE], {
                        shouldValidate: true,
                    });
                }
            } else {
                // If any other option is selected
                if (isCurrentlySelected) {
                    // If this option is currently selected, deselect it
                    setValue(
                        'insuranceCoverage',
                        currentCoverage.filter((item) => item !== coverage),
                        { shouldValidate: true },
                    );
                } else {
                    // If this option is not selected, select it and deselect "None"
                    const newCoverage = currentCoverage.filter(
                        (item) => item !== InsuranceCoverage.NONE,
                    );
                    setValue('insuranceCoverage', [...newCoverage, coverage], {
                        shouldValidate: true,
                    });
                }
            }
        },
        [watchedInsuranceCoverage, setValue],
    );

    const isFormSubmitting = isSubmitting || isUpdatingUser;

    // Should not happen, but added as a defensive check.
    if (targetUser == null) {
        captureException(new Error('Target user not found for insurance page'));
        return <Redirect href="/profile/financial-profile" />;
    }

    return (
        <ScreenContainer
            title="Insurance coverage"
            hasBackButton={true}
            subtitle={getMemberProfileUserSubtitle(authedUser, targetUser)}
        >
            {isFormSubmitting ? (
                <Spinner />
            ) : (
                <YStack gap={24} justifyContent="space-between">
                    <YStack gap={16}>
                        <YStack gap={8}>
                            <BodyMediumRegular>
                                What coverage do you currently have?
                            </BodyMediumRegular>
                            <BodyMediumRegular color="$gray1000">
                                Select all that apply.
                            </BodyMediumRegular>
                        </YStack>

                        <Form onSubmit={handleSubmit(onSubmit)}>
                            <SelectMultiple
                                options={insuranceOptions}
                                selectedIndices={Object.values(
                                    InsuranceCoverage,
                                )
                                    .map((coverage, index) =>
                                        watchedInsuranceCoverage?.includes(
                                            coverage,
                                        )
                                            ? index
                                            : -1,
                                    )
                                    .filter((index) => index !== -1)}
                                onSelect={handleInsuranceToggle}
                            />
                        </Form>
                    </YStack>

                    <Button
                        variant={ButtonVariant.PRIMARY}
                        size={ButtonSize.LARGE}
                        text="Save"
                        onPress={handleSubmit(onSubmit)}
                        disabled={!isValid || isFormSubmitting}
                    />
                </YStack>
            )}
        </ScreenContainer>
    );
};
